import React, { useState, useEffect } from 'react';
import { 
  FiDownload, FiFilter, FiCalendar, FiTrendingUp,
  FiUsers, FiEye, FiDollarSign, FiShare2,
  FiBarChart2, FiMessageSquare, FiHeart, FiRefreshCw
} from 'react-icons/fi';
import SocialMediadashboardSidebar from '../SocialMediadashboardSidebar';
import { useAuth } from '../../../Context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('7d');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [metricFilter, setMetricFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [realTimeData, setRealTimeData] = useState({});

  const { googleUserInfo } = useAuth();

  // SweetAlert2 helper functions
  const showSuccessAlert = (message) => {
    return Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK',
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const showErrorAlert = (message) => {
    return Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK',
    });
  };

  // Date range options
  const dateRanges = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '1y', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  // Platform options
  const platforms = [
    { value: 'facebook', label: 'Facebook', color: 'text-blue-600' },
    { value: 'instagram', label: 'Instagram', color: 'text-pink-600' },
    { value: 'twitter', label: 'Twitter', color: 'text-blue-400' },
    { value: 'linkedin', label: 'LinkedIn', color: 'text-blue-700' },
    { value: 'all', label: 'All Platforms', color: 'text-gray-600' }
  ];

  // Metric options
  const metrics = [
    { value: 'engagement', label: 'Engagement', icon: FiHeart },
    { value: 'reach', label: 'Reach', icon: FiEye },
    { value: 'conversions', label: 'Conversions', icon: FiTrendingUp },
    { value: 'revenue', label: 'Revenue', icon: FiDollarSign },
    { value: 'all', label: 'All Metrics', icon: FiBarChart2 }
  ];

  // Platform names mapping
  const platformNames = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    pinterest: 'Pinterest'
  };

  // Fetch Facebook Insights data with Page Access Token
  const fetchFacebookInsights = async (pageAccessToken, pageId) => {
    try {
      const response = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/insights?metric=page_impressions,page_fans,page_impressions_unique,page_engaged_users,page_views_total,page_posts_impressions&period=days_28&access_token=${pageAccessToken}`
      );
      return response.data;
    } catch (error) {
      console.error('Facebook Insights API Error:', error);
      return { data: [] };
    }
  };

  // Fetch Facebook posts for a specific page
  const fetchFacebookPosts = async (pageAccessToken, pageId) => {
    try {
      const postsResponse = await axios.get(
        `https://graph.facebook.com/v19.0/${pageId}/posts?fields=id,message,created_time,likes.limit(1).summary(true),comments.limit(1).summary(true),shares&limit=10&access_token=${pageAccessToken}`
      );
      return postsResponse.data.data || [];
    } catch (error) {
      console.error('Error fetching Facebook posts:', error);
      return [];
    }
  };

  // Generate real-time performance data from connected accounts
  const generateRealTimeData = async (accounts) => {
    if (accounts.length === 0) return {};
    
    const realData = {};
    
    for (const account of accounts) {
      if (account.platform === 'facebook' && account.access_token) {
        realData[account.account_id] = {
          accountName: account.username,
          platform: account.platform,
          pages: []
        };

        // Process ALL pages for this account
        if (account.pages && account.pages.length > 0) {
          for (const page of account.pages) {
            try {
              const insights = await fetchFacebookInsights(page.access_token, page.id);
              const posts = await fetchFacebookPosts(page.access_token, page.id);
              
              // Extract real metrics from Facebook Insights
              const impressions = insights.data?.find(item => item.name === 'page_impressions')?.values[0]?.value || 0;
              const engagedUsers = insights.data?.find(item => item.name === 'page_engaged_users')?.values[0]?.value || 0;
              const followers = insights.data?.find(item => item.name === 'page_fans')?.values[0]?.value || page.followers || 0;
              const uniqueImpressions = insights.data?.find(item => item.name === 'page_impressions_unique')?.values[0]?.value || 0;
              const views = insights.data?.find(item => item.name === 'page_views_total')?.values[0]?.value || 0;
              
              // Calculate engagement from recent posts
              let totalEngagement = 0;
              if (posts.length > 0) {
                totalEngagement = posts.reduce((sum, post) => {
                  return sum + (post.likes?.summary?.total_count || 0) + 
                         (post.comments?.summary?.total_count || 0) + 
                         (post.shares?.count || 0);
                }, 0);
              }
              
              // Calculate real engagement rate
              const engagementRate = followers > 0 ? ((engagedUsers / followers) * 100).toFixed(1) : '0.0';
              
              // Calculate estimated revenue (this would be based on your business model)
              const estimatedRevenue = (impressions * 0.02) + (engagedUsers * 0.1); // Example calculation
              
              // Store page-specific data
              realData[account.account_id].pages.push({
                pageId: page.id,
                pageName: page.name,
                metrics: {
                  followers: {
                    value: followers,
                    change: calculateChange(followers, page.followers || 0),
                    trending: followers > (page.followers || 0)
                  },
                  engagement: {
                    value: parseFloat(engagementRate),
                    change: '0%', // You could calculate this from previous data
                    trending: parseFloat(engagementRate) > 0
                  },
                  reach: {
                    value: uniqueImpressions,
                    change: '0%',
                    trending: uniqueImpressions > 0
                  },
                  impressions: {
                    value: impressions,
                    change: '0%',
                    trending: impressions > 0
                  },
                  clicks: {
                    value: Math.floor(impressions * 0.02), // Estimated CTR
                    change: '0%',
                    trending: impressions > 0
                  },
                  reactions: {
                    value: totalEngagement,
                    change: '0%',
                    trending: totalEngagement > 0
                  },
                  revenue: {
                    value: estimatedRevenue,
                    change: '0%',
                    trending: estimatedRevenue > 0
                  },
                  conversions: {
                    value: Math.floor(engagedUsers * 0.05), // Estimated conversion rate
                    change: '0%',
                    trending: engagedUsers > 0
                  }
                },
                posts: posts,
                lastUpdated: new Date().toISOString()
              });
              
            } catch (error) {
              console.error(`Failed to fetch data for page ${page.id}:`, error);
              
              // Add fallback data
              realData[account.account_id].pages.push({
                pageId: page.id,
                pageName: page.name,
                metrics: {
                  followers: { value: page.followers || 0, change: '0%', trending: false },
                  engagement: { value: 0, change: '0%', trending: false },
                  reach: { value: 0, change: '0%', trending: false },
                  impressions: { value: 0, change: '0%', trending: false },
                  clicks: { value: 0, change: '0%', trending: false },
                  reactions: { value: 0, change: '0%', trending: false },
                  revenue: { value: 0, change: '0%', trending: false },
                  conversions: { value: 0, change: '0%', trending: false }
                },
                error: true,
                lastUpdated: new Date().toISOString()
              });
            }
          }
        }
      }
    }
    
    return realData;
  };

  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return '0%';
    const change = ((current - previous) / previous) * 100;
    return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Generate real report from connected accounts
  const generateRealReport = async () => {
    setRefreshing(true);
    
    try {
      const storedAccounts = JSON.parse(localStorage.getItem('socialAccounts') || '[]');
      const realTimeData = await generateRealTimeData(storedAccounts);
      setRealTimeData(realTimeData);

      // Create reports from real data
      const newReports = [];
      
      Object.values(realTimeData).forEach(accountData => {
        accountData.pages.forEach(page => {
          // Performance Report
          newReports.push({
            id: `${accountData.accountName}-${page.pageId}-performance`,
            name: `${page.pageName} Performance Report`,
            type: 'performance',
            platforms: [accountData.platform],
            dateRange,
            createdAt: new Date(),
            metrics: {
              engagement: page.metrics.engagement.value,
              reach: page.metrics.reach.value,
              impressions: page.metrics.impressions.value,
              clicks: page.metrics.clicks.value,
              conversions: page.metrics.conversions.value,
              revenue: page.metrics.revenue.value
            },
            change: {
              engagement: parseFloat(page.metrics.engagement.change),
              reach: parseFloat(page.metrics.reach.change),
              impressions: parseFloat(page.metrics.impressions.change),
              clicks: parseFloat(page.metrics.clicks.change),
              conversions: parseFloat(page.metrics.conversions.change),
              revenue: parseFloat(page.metrics.revenue.change)
            },
            realData: true,
            pageName: page.pageName,
            accountName: accountData.accountName
          });

          // Engagement Report
          newReports.push({
            id: `${accountData.accountName}-${page.pageId}-engagement`,
            name: `${page.pageName} Engagement Analysis`,
            type: 'engagement',
            platforms: [accountData.platform],
            dateRange,
            createdAt: new Date(),
            metrics: {
              followers: page.metrics.followers.value,
              engagementRate: page.metrics.engagement.value,
              reactions: page.metrics.reactions.value,
              postEngagement: page.metrics.reactions.value / (page.posts?.length || 1),
              topPost: page.posts?.[0]?.message?.substring(0, 50) + '...' || 'No posts',
              engagementScore: (page.metrics.engagement.value * page.metrics.reactions.value / 1000).toFixed(1)
            },
            realData: true,
            pageName: page.pageName,
            accountName: accountData.accountName
          });
        });
      });

      // Platform Comparison Report
      if (Object.keys(realTimeData).length > 0) {
        const platformMetrics = {};
        Object.values(realTimeData).forEach(accountData => {
          accountData.pages.forEach(page => {
            if (!platformMetrics[accountData.platform]) {
              platformMetrics[accountData.platform] = {
                engagement: 0,
                reach: 0,
                impressions: 0,
                pages: 0
              };
            }
            platformMetrics[accountData.platform].engagement += page.metrics.engagement.value;
            platformMetrics[accountData.platform].reach += page.metrics.reach.value;
            platformMetrics[accountData.platform].impressions += page.metrics.impressions.value;
            platformMetrics[accountData.platform].pages += 1;
          });
        });

        newReports.push({
          id: 'platform-comparison',
          name: 'Platform Performance Comparison',
          type: 'competitive',
          platforms: Object.keys(platformMetrics),
          dateRange,
          createdAt: new Date(),
          metrics: {
            marketShare: Object.keys(platformMetrics).length * 25, // Simplified calculation
            shareOfVoice: 85.4,
            engagementGap: 12.7,
            followerGrowth: 8.9,
            contentFrequency: 15.2,
            sentiment: 85.4,
            ...Object.entries(platformMetrics).reduce((acc, [platform, metrics]) => {
              acc[`${platform}Engagement`] = (metrics.engagement / metrics.pages).toFixed(1);
              acc[`${platform}Reach`] = metrics.reach;
              return acc;
            }, {})
          },
          realData: true
        });
      }

      setReports(newReports);
      showSuccessAlert('Real-time report generated successfully!');
      
    } catch (error) {
      console.error('Error generating real report:', error);
      showErrorAlert('Failed to generate real-time report. Using sample data.');
      loadSampleReports();
    } finally {
      setRefreshing(false);
    }
  };

  // Load sample reports as fallback
  const loadSampleReports = () => {
    const sampleReports = [
      {
        id: '1',
        name: 'Monthly Performance Summary',
        type: 'performance',
        platforms: ['facebook', 'instagram'],
        dateRange: '30d',
        createdAt: new Date('2024-01-15'),
        metrics: {
          engagement: 24500,
          reach: 125000,
          impressions: 450000,
          clicks: 12500,
          conversions: 450,
          revenue: 12500
        },
        change: {
          engagement: 12.5,
          reach: 8.2,
          impressions: 15.7,
          clicks: -2.3,
          conversions: 25.8,
          revenue: 18.4
        },
        realData: false
      },
      // ... other sample reports
    ];
    setReports(sampleReports);
  };

  // Load reports on component mount
  useEffect(() => {
    const loadReports = async () => {
      try {
        const storedAccounts = JSON.parse(localStorage.getItem('socialAccounts') || '[]');
        setSocialAccounts(storedAccounts);
        
        if (storedAccounts.length > 0) {
          await generateRealReport();
        } else {
          loadSampleReports();
        }
      } catch (error) {
        console.error('Error loading reports:', error);
        loadSampleReports();
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, []);

  // Refresh real-time data
  const refreshRealTimeData = async () => {
    setRefreshing(true);
    await generateRealReport();
  };

  // Filter reports based on selections
  const filteredReports = reports.filter(report => {
    if (platformFilter !== 'all' && !report.platforms.includes(platformFilter) && !report.platforms.includes('all')) {
      return false;
    }
    if (metricFilter !== 'all' && report.type !== metricFilter && metricFilter !== 'all') {
      return false;
    }
    return true;
  });

  // Download report
  const downloadReport = (report) => {
    const dataStr = JSON.stringify(report, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.name.replace(/\s+/g, '_').toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    showSuccessAlert('Report downloaded successfully!');
  };

  // View report details
  const viewReport = (report) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  // Get change color
  const getChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // Get change icon
  const getChangeIcon = (change) => {
    if (change > 0) return '↗';
    if (change < 0) return '↘';
    return '→';
  };

  // Format number
  const formatNumber = (num) => {
    if (typeof num === 'number') {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
      return num.toLocaleString();
    }
    return num;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FD5F20] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <SocialMediadashboardSidebar />
      
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white shadow-sm p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
              <p className="text-gray-600">
                {socialAccounts.length > 0 
                  ? `Real-time analytics from ${socialAccounts.length} connected account${socialAccounts.length > 1 ? 's' : ''}`
                  : 'Analyze your social media performance with real-time data'
                }
              </p>
            </div>
            <div className="flex space-x-3">
              {socialAccounts.length > 0 && (
                <button
                  onClick={refreshRealTimeData}
                  disabled={refreshing}
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  <FiRefreshCw className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </button>
              )}
              <button
                onClick={generateRealReport}
                disabled={refreshing}
                className="flex items-center bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d] disabled:opacity-50"
              >
                <FiTrendingUp className="mr-2" />
                Generate Report
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mt-6">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              {dateRanges.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>

            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              {platforms.map(platform => (
                <option key={platform.value} value={platform.value}>
                  {platform.label}
                </option>
              ))}
            </select>

            <select
              value={metricFilter}
              onChange={(e) => setMetricFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
            >
              {metrics.map(metric => (
                <option key={metric.value} value={metric.value}>
                  {metric.label}
                </option>
              ))}
            </select>
          </div>

          {/* Real-time Data Status */}
          {socialAccounts.length > 0 && (
            <div className="mt-4 flex items-center text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              Connected to real-time social media data
              <span className="ml-2 text-gray-500">
                • Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          )}
        </header>

        {/* Reports Grid */}
        <main className="p-6">
          {filteredReports.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiBarChart2 className="text-gray-400 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Reports Found</h3>
              <p className="text-gray-600 mb-6">
                {socialAccounts.length > 0 
                  ? 'Generate your first real-time report to analyze performance'
                  : 'Connect social media accounts to generate real-time reports'
                }
              </p>
              <button 
                onClick={generateRealReport}
                className="bg-[#FD5F20] text-white px-4 py-2 rounded-lg hover:bg-[#e55a1d]"
              >
                {socialAccounts.length > 0 ? 'Generate Real-Time Report' : 'Connect Accounts First'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredReports.map(report => (
                <div key={report.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Real-time data indicator */}
                  {report.realData && (
                    <div className="bg-green-50 px-4 py-1 border-b border-green-200">
                      <div className="flex items-center text-green-700 text-xs">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                        Real-time Data • {report.pageName || platformNames[report.platforms[0]]}
                      </div>
                    </div>
                  )}
                  
                  <div className="p-6">
                    {/* Report Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-1">
                          {report.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {dateRanges.find(r => r.value === report.dateRange)?.label}
                          {report.accountName && ` • ${report.accountName}`}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        report.realData 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {report.type} {report.realData ? '• Live' : ''}
                      </span>
                    </div>

                    {/* Platforms */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {report.platforms.map(platform => {
                        const platformInfo = platforms.find(p => p.value === platform);
                        return platformInfo ? (
                          <span key={platform} className={`text-xs px-2 py-1 rounded ${platformInfo.color} bg-gray-50`}>
                            {platformInfo.label}
                          </span>
                        ) : null;
                      })}
                    </div>

                    {/* Key Metrics */}
                    <div className="space-y-3 mb-4">
                      {Object.entries(report.metrics).slice(0, 3).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </span>
                          <div className="flex items-center">
                            <span className="font-semibold text-gray-900 mr-2">
                              {key.includes('revenue') ? formatCurrency(value) : 
                               key.includes('Rate') || key.includes('Score') ? `${value}%` : 
                               formatNumber(value)}
                            </span>
                            {report.change && report.change[key] !== undefined && (
                              <span className={`text-xs ${getChangeColor(report.change[key])}`}>
                                {getChangeIcon(report.change[key])} {Math.abs(report.change[key])}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500">
                        {new Date(report.createdAt).toLocaleDateString()} at {new Date(report.createdAt).toLocaleTimeString()}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => viewReport(report)}
                          className="flex items-center text-blue-600 hover:text-blue-700 text-sm"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => downloadReport(report)}
                          className="flex items-center text-gray-600 hover:text-gray-700 text-sm"
                        >
                          <FiDownload className="mr-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Report Detail Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">{selectedReport.name}</h2>
                  {selectedReport.realData && (
                    <div className="flex items-center text-green-600 text-sm mt-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                      Real-time Data • {selectedReport.pageName || platformNames[selectedReport.platforms[0]]}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>
              <p className="text-gray-600 mt-1">
                Generated on {new Date(selectedReport.createdAt).toLocaleDateString()} at {new Date(selectedReport.createdAt).toLocaleTimeString()} • 
                {dateRanges.find(r => r.value === selectedReport.dateRange)?.label}
                {selectedReport.accountName && ` • ${selectedReport.accountName}`}
              </p>
            </div>
            
            <div className="p-6">
              {/* Platforms */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Platforms</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedReport.platforms.map(platform => {
                    const platformInfo = platforms.find(p => p.value === platform);
                    return platformInfo ? (
                      <span key={platform} className={`px-3 py-1 rounded-full text-sm ${platformInfo.color} bg-gray-50 border`}>
                        {platformInfo.label}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {Object.entries(selectedReport.metrics).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      </span>
                      {selectedReport.change && selectedReport.change[key] !== undefined && (
                        <span className={`text-xs font-medium ${getChangeColor(selectedReport.change[key])}`}>
                          {getChangeIcon(selectedReport.change[key])} {Math.abs(selectedReport.change[key])}%
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {key.includes('revenue') ? formatCurrency(value) : 
                       key.includes('Rate') || key.includes('Score') ? `${value}%` : 
                       formatNumber(value)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Performance Summary */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {selectedReport.realData ? 'Real-time Performance Summary' : 'Performance Summary'}
                </h3>
                <p className="text-blue-800 text-sm">
                  {selectedReport.type === 'performance' && 
                    'Overall performance shows strong engagement growth with positive trends across key metrics.'}
                  {selectedReport.type === 'engagement' && 
                    'Engagement analysis reveals audience interaction patterns and content performance.'}
                  {selectedReport.type === 'competitive' && 
                    'Platform comparison shows performance differences and optimization opportunities.'}
                  {selectedReport.realData && ' Data is sourced directly from social media platforms in real-time.'}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => downloadReport(selectedReport)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <FiDownload className="mr-2" />
                Download JSON
              </button>
              <button className="flex items-center px-4 py-2 bg-[#FD5F20] text-white rounded-lg hover:bg-[#e55a1d]">
                <FiShare2 className="mr-2" />
                Share Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;